import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { useAppDispatch, useAppSelector } from 'store'
import $resource, { ResourceType } from 'store/resource'
import $business from 'store/business'

import App from 'components/App'

import styles from './AppointmentResources.module.scss'

const AppointmentResources: React.FC = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const resources = useAppSelector($resource.get.filtered)
  const isResourcesDisabled = useAppSelector($resource.get.isDisabled)
  const selectedResourceId = useAppSelector(({ $resource }) => $resource.selectedId)
  const selectedResourceAny = useAppSelector(({ $resource }) => $resource.selectedAny)
  const settings = useAppSelector($business.get.settings)
  const isRtl = useAppSelector(({ $app }) => $app.isRtl)

  const wrapRef = useRef<HTMLDivElement | null>(null)
  const rowRef = useRef<HTMLDivElement | null>(null)
  const suppressClickRef = useRef(false)

  useEffect(() => {
    if (selectedResourceId) {
      const resource = resources.find((item: ResourceType) => item.id === selectedResourceId)
      if ( ! resource) {
        dispatch($resource.set.selectedId(null))
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceId, resources])

  const handleSelect = (id: number | string) => () => {
    if (suppressClickRef.current) { 
      suppressClickRef.current = false
      return
    }

    if (!isResourcesDisabled) {
      const numberId = Number(id)
      dispatch($resource.set.selectedId(selectedResourceId === numberId ? null : numberId))
    }
  }

  const handleSelectAny = () => () => {
    if (suppressClickRef.current) { 
      suppressClickRef.current = false
      return
    }

    if (isResourcesDisabled) {
      return
    }
    dispatch($resource.set.selectedAny(!selectedResourceAny))
  }

  return resources.length ? (
    <App.Flex column gap={4} ref={wrapRef} id="appointment-resources">
      <App.Text bold height={1}>{t('Select specialist')}:</App.Text>

      <App.Flex className={cn(styles.scrollerWrap, {[styles.rtl]: isRtl})}>
        <App.Flex row fullWidth grid={3} gap={3} className={cn(styles.scroll)} ref={rowRef} dir={isRtl ? 'rtl' : 'ltr'}>
          {settings.allow_first_available_resource && (
            <App.Flex column aCenter gap={1} className={cn(styles.resource, {[styles.active]: selectedResourceAny && !isResourcesDisabled}, {[styles.disabled]: isResourcesDisabled})} onClick={handleSelectAny()}>
              <App.Flex center className={styles.photo}>
                <App.Flex center className={styles.inner}>
                  <App.Icon icon="user" size={48} />
                  <App.Flex center className={styles.check}>
                    <App.Icon icon="check" white size={40} />
                  </App.Flex>
                </App.Flex>
              </App.Flex>

              <App.Text center xs weight={selectedResourceAny ? 'wBold' : 'wMedium'}>{t('Any available')}</App.Text>
            </App.Flex>
          )}

          {resources.map((item: ResourceType) => {
            return (
              <App.Flex key={item.id} column aCenter gap={1} className={cn(styles.resource, {[styles.active]: selectedResourceId === item.id && !isResourcesDisabled}, {[styles.disabled]: isResourcesDisabled})} onClick={handleSelect(item.id)}>
                <App.Flex center className={styles.photo}>
                  <App.Flex center className={styles.inner}>
                    {item.avatar !== '' ? (
                      <img src={item.avatar} alt="" draggable={false} onDragStart={(e) => e.preventDefault()} />
                    ) : (
                      <App.Icon icon="user" size={48} />
                    )}

                    <App.Flex center className={styles.check}>
                      <App.Icon icon="check" white size={40} />
                    </App.Flex>
                  </App.Flex>
                </App.Flex>

                <App.Text center xs weight={selectedResourceId === item.id ? 'wBold' : 'wMedium'}>{item.name}</App.Text>
              </App.Flex>
            )}
          )}
        </App.Flex>
      </App.Flex>
    </App.Flex>
  ) : null
}

export default AppointmentResources